import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Stepper, Step, StepLabel, Paper, Button, Grid,
  TextField, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel,
  Chip, Card, CardContent, List, ListItem, ListItemText, Divider,
  IconButton, Stack, Alert, Tab, Tabs, Checkbox, Dialog, DialogTitle, InputLabel,
  DialogContent, DialogContentText, DialogActions, CircularProgress, Switch,
  Accordion, AccordionSummary, AccordionDetails, Slider, InputAdornment, MenuItem, Select,
  Collapse
} from '@mui/material';
import { 
  Domain as DomainIcon,
  Psychology as PsychologyIcon,
  Extension as ExtensionIcon,
  Security as SecurityIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Celebration as CelebrationIcon,
  AccountBalance as AccountBalanceIcon,
  Gavel as GavelIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  SupportAgent as SupportAgentIcon,
  Business as BusinessIcon,
  Computer as ComputerIcon,
  Campaign as CampaignIcon,
  ExpandMore as ExpandMoreIcon,
  ChatBubble as ChatBubbleIcon,
  Mood as MoodIcon,
  Settings as SettingsIcon,
  Engineering as EngineeringIcon,
  GitHub as GitHubIcon, 
  Email as EmailIcon,
  Storage as StorageIcon,
  Language as LanguageIcon,
  Notifications as NotificationsIcon,
  CloudQueue as CloudQueueIcon,
  Api as ApiIcon,
  Schedule as ScheduleIcon,
  Webhook as WebhookIcon,
  SettingsEthernet as SettingsEthernetIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Memory as MemoryIcon,
  BarChart as BarChartIcon,
  Image as ImageIcon,
  Calculate as CalculateIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  Code as CodeIcon,
  Groups as GroupsIcon,
  Mail as MailIcon,
} from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useTheme } from '@mui/material/styles';
import { DEMO_MODELS, hardwareOptions } from '../../helpers/MockData';
import AgentStatusOverview from './AgentStatusOverview';
import { modelCategories, getModelsByType, getPerformanceColor, getCostDisplay, getModelById } from './ModelMapping';
import { AgentTemplates } from './AgentTemplates';
import { useKeycloak } from '@react-keycloak/web';
import { createAgent, getAvailableEndpoints } from '../../api/services/agent-studio/AgentManagement';
import { getModels } from '../../api/services/agent-studio/AgentFeatures';

// Add this near the top of the file with other imports
const ModelCard = ({ model, selected, onSelect }) => {
  return (
    <Card 
      sx={{ 
        cursor: 'pointer',
        border: selected ? '2px solid' : '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        height: '100%',
        transition: 'all 0.2s'
      }}
      onClick={() => onSelect(model.id)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="subtitle1">{model.name}</Typography>
          <Chip 
            label={model.provider}
            size="small"
            variant="outlined"
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {model.description}
        </Typography>
        
        <Stack direction="row" spacing={1}>
          <Chip 
            label={`Context: ${model.contextWindow}`}
            size="small"
            variant="outlined"
          />
          <Chip 
            label={model.performance}
            size="small"
            color={getPerformanceColor(model.performance)}
          />
          <Chip 
            label={getCostDisplay(model.cost).label}
            size="small"
            color={getCostDisplay(model.cost).color}
          />
          {model.finetuned && (
            <Chip 
              label="Fine-tuned"
              size="small"
              color="secondary"
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

// Step 1: Select Domain & Define Tasks
export const DomainAndTasks = ({ agentConfig, setAgentConfig, onNext, onCancel }) => {
  const [templates, setTemplates] = useState([]);
  const [hoveredTemplateId, setHoveredTemplateId] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskDialogTemplate, setTaskDialogTemplate] = useState(null);
  const [dialogTasks, setDialogTasks] = useState([]);
  const [dialogSelectedTasks, setDialogSelectedTasks] = useState([]);
  const [newTask, setNewTask] = useState('');

  const iconMap = {
    AccountBalance: AccountBalanceIcon,
    Gavel: GavelIcon,
    People: PeopleIcon,
    AttachMoney: AttachMoneyIcon,
    SupportAgent: SupportAgentIcon,
    Business: BusinessIcon,
    Computer: ComputerIcon,
    Campaign: CampaignIcon
  };

  React.useEffect(() => {
    const templatesArray = Object.entries(AgentTemplates).map(([key, value]) => ({
      id: key,
      name: key,
      description: value.description,
      task_list: value.task_list,
      prompt_template: value.prompt_template,
      icon: value.icon,
      languages: value.languages || ['english']
    }));
    setTemplates(templatesArray);
    if (!agentConfig.templateId && templatesArray.length > 0) {
      handleTemplateSelect(templatesArray[0].id, templatesArray);
    }
    if (!agentConfig.languages) {
      setAgentConfig(prev => ({ 
        ...prev, 
        languages: ['english'] 
      }));
    }
  }, []);

  const handleTemplateSelect = (templateId, templatesArr = templates) => {
    setCustomMode(templateId === 'custom');
    if (templateId === 'custom') {
      setAgentConfig(prev => ({
        ...prev,
        name: '',
        templateId: 'custom',
        tasks: [],
        systemPrompt: ''
      }));
      setSelectedTasks([]);
      return;
    }
    const template = (templatesArr.length ? templatesArr : templates).find(t => t.id === templateId);
    if (template) {
      setAgentConfig(prev => ({
        ...prev,
        name: template.name,
        systemPrompt: template.prompt_template,
        templateId: templateId,
        tasks: template.task_list,
        languages: template.languages || ['english']
      }));
      setSelectedTasks(template.task_list);
    }
  };

  const openTaskDialog = (template, e) => {
    if (e) e.stopPropagation();
    setTaskDialogTemplate(template);
    setDialogTasks([...template.task_list]);
    setDialogSelectedTasks(selectedTasks.length ? [...selectedTasks] : [...template.task_list]);
    setTaskDialogOpen(true);
    setNewTask('');
  };

  const handleDialogTaskToggle = (task) => {
    let updated;
    if (dialogSelectedTasks.includes(task)) {
      updated = dialogSelectedTasks.filter(t => t !== task);
    } else {
      updated = [...dialogSelectedTasks, task];
    }
    setDialogSelectedTasks(updated);
  };

  const handleAddDialogTask = () => {
    if (newTask.trim() && !dialogTasks.includes(newTask.trim())) {
      setDialogTasks([...dialogTasks, newTask.trim()]);
      setDialogSelectedTasks([...dialogSelectedTasks, newTask.trim()]);
      setNewTask('');
    }
  };

  const handleDialogSave = () => {
    setSelectedTasks(dialogSelectedTasks);
    setAgentConfig(prev => ({ ...prev, tasks: dialogSelectedTasks }));
    setTaskDialogOpen(false);
  };

  const handleDialogCancel = () => {
    setTaskDialogOpen(false);
  };
  
  const handleLanguageToggle = (language) => {
    setAgentConfig(prev => {
      const currentLanguages = prev.languages || [];
      if (currentLanguages.includes(language) && currentLanguages.length > 1) {
        return {
          ...prev,
          languages: currentLanguages.filter(lang => lang !== language)
        };
      }
      if (!currentLanguages.includes(language)) {
        return {
          ...prev,
          languages: [...currentLanguages, language]
        };
      }
      return prev;
    });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>Select Domain & Define Tasks</Typography>
      <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Agent Name"
              value={agentConfig.name}
              onChange={(e) => setAgentConfig(prev => ({ ...prev, name: e.target.value }))}
              required
              error={!agentConfig.name}
          helperText={!agentConfig.name ? "Please enter a name for your agent" : ""}
              placeholder="Enter a descriptive name for your agent"
            />
        </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Language</InputLabel>
            <Select
              value={agentConfig.language || 'en'}
              label="Language"
              onChange={(e) => setAgentConfig(prev => ({ ...prev, language: e.target.value }))}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="de">German</MenuItem>
              <MenuItem value="es">Spanish</MenuItem>
              <MenuItem value="fr">French</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>Choose a Pre-configured Agent Template or create your own</Typography>
      <Grid container spacing={2}>
        {templates.map((template) => {
          const IconComponent = iconMap[template.icon];
          return (
            <Grid item xs={12} sm={6} md={4} key={template.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: agentConfig.templateId === template.id ? '2px solid' : '1px solid',
                  borderColor: agentConfig.templateId === template.id ? 'primary.main' : 'divider',
                  transition: 'all 0.2s',
                  minHeight: 180,
                  boxShadow: agentConfig.templateId === template.id ? 4 : 1,
                  position: 'relative',
                  background: hoveredTemplateId === template.id ? 'rgba(0,0,0,0.03)' : 'background.paper',
                  zIndex: hoveredTemplateId === template.id ? 2 : 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  p: 2
                }}
                onMouseEnter={() => setHoveredTemplateId(template.id)}
                onMouseLeave={() => setHoveredTemplateId(null)}
                onClick={() => handleTemplateSelect(template.id)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {IconComponent && <IconComponent sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />}
                  <Typography variant="subtitle1" fontWeight={600}>{template.name}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>{template.description}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={e => { e.stopPropagation(); openTaskDialog(template, e) }}
                    disabled={agentConfig.templateId !== template.id}
                  >
                    Configure Tasks
                  </Button>
                </Box>
              </Card>
            </Grid>
          );
        })}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              cursor: 'pointer',
              border: agentConfig.templateId === 'custom' ? '2px solid' : '1px dashed',
              borderColor: agentConfig.templateId === 'custom' ? 'primary.main' : 'divider',
              transition: 'all 0.2s',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: agentConfig.templateId === 'custom' ? 3 : 0,
              bgcolor: 'background.paper',
              '&:hover': {
                boxShadow: 2,
                borderColor: 'primary.light'
              }
            }}
            onClick={() => handleTemplateSelect('custom')}
          >
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <ExtensionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="subtitle1" fontWeight={600}>Custom Agent</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                Create your own custom agent with specific capabilities
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Dialog open={taskDialogOpen} onClose={handleDialogCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Configure Tasks for {taskDialogTemplate?.name}</DialogTitle>
        <DialogContent>
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Select or add tasks for this agent:</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 300, overflow: 'auto', mb: 2 }}>
              {dialogTasks.map((task, idx) => (
                <Button
                  key={idx}
                  variant={dialogSelectedTasks.includes(task) ? 'contained' : 'outlined'}
                  color={dialogSelectedTasks.includes(task) ? 'primary' : 'inherit'}
                  size="small"
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', borderRadius: 2, fontSize: 14, fontWeight: 400, pl: 1.5, display: 'flex', alignItems: 'center' }}
                  onClick={() => handleDialogTaskToggle(task)}
                >
                  {dialogSelectedTasks.includes(task) && <CheckIcon sx={{ fontSize: 18, mr: 1 }} />}
                  {task}
                </Button>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
          <TextField
                size="small"
                label="Add New Task"
                value={newTask}
                onChange={e => setNewTask(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddDialogTask() } }}
                sx={{ flex: 1 }}
              />
              <Button variant="contained" size="small" onClick={handleAddDialogTask} disabled={!newTask.trim()}>Add</Button>
        </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogCancel}>Cancel</Button>
          <Button onClick={handleDialogSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ mt: 3, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? 'Hide Advanced Settings' : 'Show Advanced Settings'}
        </Button>
      </Box>
      <Collapse in={showAdvanced}>
        <Paper sx={{ 
          p: 3, 
          bgcolor: 'background.paper', 
          mt: 2, 
          borderRadius: 2, 
          borderLeft: '4px solid', 
          borderColor: 'primary.main',
          position: 'relative'
        }}>
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 2,
            zIndex: 1,
            pointerEvents: 'none'
          }} />
          <Box sx={{ 
            position: 'relative',
            opacity: 0.6,
            pointerEvents: 'none'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Advanced Agent Configuration
              </Typography>
              <Chip 
                label="Coming Soon" 
                size="small" 
                color="warning" 
                sx={{ ml: 2 }}
              />
            </Box>
            <Box sx={{ mb: 4 }}>
              <Card sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Agent Behavior</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel>Persona Style</FormLabel>
                      <RadioGroup
                        row
                        value={agentConfig.personaStyle || 'friendly'}
                        onChange={(e) => setAgentConfig(prev => ({ ...prev, personaStyle: e.target.value }))}
                      >
                        <FormControlLabel value="friendly" control={<Radio />} label="Friendly" />
                        <FormControlLabel value="formal" control={<Radio />} label="Formal" />
                        <FormControlLabel value="technical" control={<Radio />} label="Technical" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel>Agent Type</FormLabel>
                      <RadioGroup
                        row
                        value={agentConfig.agentType || 'reactive'}
                        onChange={(e) => setAgentConfig(prev => ({ ...prev, agentType: e.target.value }))}
                      >
                        <FormControlLabel value="reactive" control={<Radio />} label="Reactive" />
                        <FormControlLabel value="proactive" control={<Radio />} label="Proactive" />
                        <FormControlLabel value="autonomous" control={<Radio />} label="Autonomous" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </Card>
            </Box>
            <Box sx={{ mb: 4 }}>
              <Card sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Language Settings</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Agent Languages (select at least one)</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {['english', 'spanish', 'french', 'german', 'chinese', 'japanese', 'arabic', 'hindi', 'russian'].map((lang) => (
                        <Chip
                          key={lang}
                          label={lang.charAt(0).toUpperCase() + lang.slice(1)}
                          onClick={() => handleLanguageToggle(lang)}
                          color={agentConfig.languages?.includes(lang) ? "primary" : "default"}
                          variant={agentConfig.languages?.includes(lang) ? "filled" : "outlined"}
                          clickable
                          sx={{ m: 0.5 }}
                        />
                      ))}
                      <Chip
                        label="Custom"
                        onClick={() => handleLanguageToggle('custom')}
                        color={agentConfig.languages?.includes('custom') ? "primary" : "default"}
                        variant={agentConfig.languages?.includes('custom') ? "filled" : "outlined"}
                        clickable
                        sx={{ m: 0.5 }}
                      />
                    </Box>
                    {agentConfig.languages?.includes('custom') && (
                      <TextField
                        fullWidth
                        label="Specify Custom Language"
                        placeholder="e.g., Portuguese, Korean, etc."
                        value={agentConfig.customLanguage || ''}
                        onChange={(e) => setAgentConfig(prev => ({...prev, customLanguage: e.target.value}))}
                        sx={{ mt: 2 }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>Multilingual Response</Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        value={agentConfig.multilingual || 'yes'}
                        onChange={(e) => setAgentConfig(prev => ({...prev, multilingual: e.target.value}))}
                      >
                        <FormControlLabel value="yes" control={<Radio />} label="Yes - Respond in the language of the query" />
                        <FormControlLabel value="no" control={<Radio />} label="No - Always respond in the primary language" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </Card>
            </Box>
          </Box>
        </Paper>
        </Collapse>
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{ minWidth: 120 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={!agentConfig.name}
          endIcon={<ArrowForwardIcon />}
          sx={{ minWidth: 120 }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

// Step 2: Configure Intelligence
export const ConfigureIntelligence = ({ agentConfig, setAgentConfig, onNext, onBack }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newWebsite, setNewWebsite] = useState({ url: '', name: '', scrapeFrequency: 'daily', subdomainDepth: 2 });
  const [newDatabase, setNewDatabase] = useState({ name: '', type: 'sql', connectionString: '', tables: '' });
  const [showAdvancedModel, setShowAdvancedModel] = useState(false);
  const [showMemorySettings, setShowMemorySettings] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [apiModels, setApiModels] = useState([]);
  const { keycloak } = useKeycloak();

  useEffect(() => {
    // Fetch available models from API when component loads
    const fetchAvailableModels = async () => {
      try {
        setLoadingModels(true);
        await keycloak.updateToken(30);
        
        // Try to fetch models from the API
        const response = await getModels("dummy-key");
        if (response && response.data) {
          const modelList = response.data.map(model => ({
            id: model.key,
            name: model.key.split('/').pop(),
            value: model.key,
            provider: model.key.split('/')[0],
            description: `Model from ${model.key.split('/')[0]}`,
            contextWindow: "4K-32K",
            performance: "High",
            cost: "Medium",
            finetuned: false,
            features: ["rag"] // All models are RAG-enabled
          }));
          setApiModels(modelList);
        } else {
          // Fall back to mock data
          const modelList = DEMO_MODELS.map(model => ({
            id: model.id || model.value,
            name: model.name || model.id?.split('/').pop() || model.value?.split('/').pop(),
            value: model.value || model.id,
            provider: model.provider || model.value?.split('/')[0] || 'Provider',
            description: model.description || `Model from ${model.provider || 'unknown provider'}`,
            contextWindow: model.contextWindow || "4K-32K",
            performance: model.performance || "High",
            cost: model.cost || "Medium",
            finetuned: model.finetuned || false,
            features: ["rag"] // All models are RAG-enabled
          }));
          setApiModels(modelList);
        }
      } catch (error) {
        console.error("Error fetching models:", error);
        // Fall back to mock data
        setApiModels(DEMO_MODELS);
      } finally {
        setLoadingModels(false);
      }
    };

    fetchAvailableModels();
  }, [keycloak]);

  const handleModelSelect = (modelId) => {
    setAgentConfig(prev => {
      // Check if model is already selected
      const isSelected = prev.selectedModels?.includes(modelId);
      
      if (isSelected) {
        // If removing the default model, set a new default if possible
        const newSelectedModels = prev.selectedModels.filter(id => id !== modelId);
        let newDefaultModel = prev.selectedModelId;
        
        if (modelId === prev.selectedModelId) {
          newDefaultModel = newSelectedModels.length > 0 ? newSelectedModels[0] : '';
        }
        
        return {
          ...prev,
          selectedModels: newSelectedModels,
          selectedModelId: newDefaultModel,
          model: newDefaultModel
        };
      } else {
        // Add model to selected models
        const newSelectedModels = [...(prev.selectedModels || []), modelId];
        // If this is the first selected model, make it the default
        const newDefaultModel = prev.selectedModelId || modelId;
        
        return {
          ...prev,
          selectedModels: newSelectedModels,
          selectedModelId: newDefaultModel,
          model: newDefaultModel
        };
      }
    });
  };
  
  const handleSetDefaultModel = (modelId) => {
    if (agentConfig.selectedModels?.includes(modelId)) {
    setAgentConfig(prev => ({
      ...prev,
        selectedModelId: modelId,
        model: modelId
    }));
    }
  };

  const availableModels = apiModels;
    
  // Knowledge handling functions
  const handleAddDocument = (documentId) => {
    setAgentConfig(prev => ({
      ...prev,
      documents: prev.documents.includes(documentId)
        ? prev.documents.filter(id => id !== documentId)
        : [...prev.documents, documentId]
    }));
  };
  
  const handleAddWebsite = () => {
    if (newWebsite.url && newWebsite.name) {
      const websiteId = `website-${Date.now()}`;
      setAgentConfig(prev => ({
        ...prev,
        websites: [...(prev.websites || []), { id: websiteId, ...newWebsite }]
      }));
      setNewWebsite({ url: '', name: '', scrapeFrequency: 'daily', subdomainDepth: 2 });
      setShowAddForm(false);
    }
  };
  
  const handleRemoveWebsite = (websiteId) => {
    setAgentConfig(prev => ({
      ...prev,
      websites: (prev.websites || []).filter(site => site.id !== websiteId)
    }));
  };
  
  const handleAddDatabase = () => {
    if (newDatabase.name && newDatabase.connectionString) {
      const dbId = `db-${Date.now()}`;
      setAgentConfig(prev => ({
        ...prev,
        databases: [...(prev.databases || []), { id: dbId, ...newDatabase }]
      }));
      setNewDatabase({ name: '', type: 'sql', connectionString: '', tables: '' });
      setShowAddForm(false);
    }
  };
  
  const handleRemoveDatabase = (dbId) => {
    setAgentConfig(prev => ({
      ...prev,
      databases: (prev.databases || []).filter(db => db.id !== dbId)
    }));
  };
  
  const handleConnectAPI = (apiId) => {
    setAgentConfig(prev => ({
      ...prev,
      connectedAPIs: prev.connectedAPIs.includes(apiId)
        ? prev.connectedAPIs.filter(id => id !== apiId)
        : [...prev.connectedAPIs, apiId]
    }));
  };
  
  const mockDocuments = [
    { id: 'doc1', name: 'Product Documentation.pdf', type: 'PDF', size: '2.4 MB' },
    { id: 'doc2', name: 'Customer FAQs.docx', type: 'DOCX', size: '1.1 MB' },
    { id: 'doc3', name: 'Sales Data 2023.csv', type: 'CSV', size: '3.7 MB' },
    { id: 'doc4', name: 'API Documentation.json', type: 'JSON', size: '0.8 MB' }
  ];
  
  const mockAPIs = [
    { id: 'api1', name: 'Product Catalog API', endpoint: '/api/products', status: 'Connected' },
    { id: 'api2', name: 'Customer Database', endpoint: '/api/customers', status: 'Not Connected' },
    { id: 'api3', name: 'Weather Service', endpoint: 'https://api.weather.com', status: 'Not Connected' }
  ];
  
  const renderAddForm = () => {
    switch (activeTab) {
      case 0: // Documents
        return null; // Document upload form would go here
      case 1: // APIs
        return null; // API connection form would go here  
      case 2: // Databases
        return (
          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Add Database Connection</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Database Name"
                  value={newDatabase.name}
                  onChange={(e) => setNewDatabase(prev => ({ ...prev, name: e.target.value }))}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <FormLabel id="database-type">Database Type</FormLabel>
                  <RadioGroup
                    row
                    value={newDatabase.type}
                    onChange={(e) => setNewDatabase(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <FormControlLabel value="sql" control={<Radio size="small" />} label="SQL" />
                    <FormControlLabel value="nosql" control={<Radio size="small" />} label="NoSQL" />
                    <FormControlLabel value="vector" control={<Radio size="small" />} label="Vector DB" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Connection String"
                  value={newDatabase.connectionString}
                  onChange={(e) => setNewDatabase(prev => ({ ...prev, connectionString: e.target.value }))}
                  placeholder="postgresql://user:password@localhost:5432/mydatabase"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tables/Collections (comma separated)"
                  value={newDatabase.tables}
                  onChange={(e) => setNewDatabase(prev => ({ ...prev, tables: e.target.value }))}
                  placeholder="users, products, orders"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button variant="outlined" size="small" onClick={() => setShowAddForm(false)}>Cancel</Button>
                  <Button 
                    variant="contained" 
                    size="small" 
                    onClick={handleAddDatabase}
                    disabled={!newDatabase.name || !newDatabase.connectionString}
                  >
                    Add Database
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        );
      case 3: // URL
        return (
          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Add URL for Scraping</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="URL Name"
                  value={newWebsite.name}
                  onChange={(e) => setNewWebsite(prev => ({ ...prev, name: e.target.value }))}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="URL"
                  value={newWebsite.url}
                  onChange={(e) => setNewWebsite(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://example.com"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <FormLabel id="scrape-frequency">Scrape Frequency</FormLabel>
                  <RadioGroup
                    row
                    value={newWebsite.scrapeFrequency}
                    onChange={(e) => setNewWebsite(prev => ({ ...prev, scrapeFrequency: e.target.value }))}
                  >
                    <FormControlLabel value="daily" control={<Radio size="small" />} label="Daily" />
                    <FormControlLabel value="weekly" control={<Radio size="small" />} label="Weekly" />
                    <FormControlLabel value="monthly" control={<Radio size="small" />} label="Monthly" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button variant="outlined" size="small" onClick={() => setShowAddForm(false)}>Cancel</Button>
                  <Button 
                    variant="contained" 
                    size="small" 
                    onClick={handleAddWebsite}
                    disabled={!newWebsite.url || !newWebsite.name}
                  >
                    Add Website
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Configure Intelligence</Typography>
      
      <Box sx={{ mb: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Select Models
          </Typography>
        </Box>
          
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select one or more models for your agent. All models support RAG capabilities. The default model will be used if no specific model is specified in API calls.
        </Typography>

        {loadingModels ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
        <Grid container spacing={2}>
          {availableModels.map((model) => {
            const isSelected = agentConfig.selectedModels?.includes(model.id);
            const isDefault = agentConfig.selectedModelId === model.id;
            
            return (
              <Grid item xs={12} md={6} key={model.id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: isSelected ? '2px solid' : '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    height: '100%',
                    transition: 'all 0.2s',
                    position: 'relative',
                    pb: isSelected ? 3 : 0 // Add padding at bottom for selected cards
                  }}
                  onClick={() => handleModelSelect(model.id)}
                >
                  {isSelected && (
                    <Box sx={{ 
                      position: 'absolute', 
                      top: '8px', 
                      right: '8px', 
                      zIndex: 1,
                      display: 'flex',
                      flexDirection: 'column', // Changed from row to column to avoid overlap
                      alignItems: 'flex-end',
                      gap: 1
                    }}>
                      <Chip 
                        label="Selected" 
                        color="primary" 
                        size="small"
                      />
                      {isDefault ? (
                        <Chip 
                          label="Default" 
                          color="secondary"
                          size="small"
                        />
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetDefaultModel(model.id);
                          }}
                          sx={{ mt: 1 }}
                        >
                          Set as Default
                        </Button>
                      )}
                    </Box>
                  )}
                  
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle1">{model.name}</Typography>
                      <Chip 
                        label={model.provider}
                        size="small"
                        variant="outlined"
                        sx={{ mr: isSelected ? 10 : 0 }} // Add margin when selected to avoid overlap
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {model.description}
                    </Typography>
                    
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip 
                        label={`Context: ${model.contextWindow}`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip 
                        label={model.performance}
                        size="small"
                        color={getPerformanceColor(model.performance)}
                      />
                      <Chip 
                        label={getCostDisplay(model.cost).label}
                        size="small"
                        color={getCostDisplay(model.cost).color}
                      />
                      {model.finetuned && (
                        <Chip 
                          label="Fine-tuned"
                          size="small" 
                          color="secondary"
                        />
                      )}
                      <Chip
                        label="RAG-enabled"
                        size="small"
                        color="success"
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          {availableModels.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="info">
                No models available. Please check your connection or try again later.
              </Alert>
            </Grid>
          )}
        </Grid>
        )}
      </Box>
      
      {/* Data Sources Section */}
      <Box sx={{ mt: 5, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Data Sources</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure these data sources after deployment to enhance your model's capabilities.
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <StorageIcon color="primary" sx={{ fontSize: 48 }} />
              </Box>
              <Typography variant="subtitle1" fontWeight={500} textAlign="center" gutterBottom>
                Documents
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Manage your documents
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <ApiIcon color="primary" sx={{ fontSize: 48 }} />
              </Box>
              <Typography variant="subtitle1" fontWeight={500} textAlign="center" gutterBottom>
                APIs
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Integrate with APIs
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <StorageIcon color="primary" sx={{ fontSize: 48 }} />
              </Box>
              <Typography variant="subtitle1" fontWeight={500} textAlign="center" gutterBottom>
                Databases
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Set up databases
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <LanguageIcon color="primary" sx={{ fontSize: 48 }} />
              </Box>
              <Typography variant="subtitle1" fontWeight={500} textAlign="center" gutterBottom>
                URLs
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Configure URLs for scraping
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>
      
      <Box sx={{ mt: 4, mb: 4 }}>
          <Button
            variant="outlined"
            startIcon={showAdvancedModel ? <ExpandMoreIcon /> : <SettingsIcon />}
            onClick={() => setShowAdvancedModel(!showAdvancedModel)}
            sx={{ mb: 2 }}
          >
            {showAdvancedModel ? 'Hide Advanced Settings' : 'Show Advanced Settings'}
          </Button>
          <Collapse in={showAdvancedModel}>
            <Paper sx={{ 
              p: 3, 
              bgcolor: 'background.paper', 
              mt: 2, 
              mb: 3, 
              borderRadius: 2, 
              borderLeft: '4px solid', 
              borderColor: 'primary.main',
              position: 'relative'
            }}>
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 2,
                zIndex: 1,
                pointerEvents: 'none'
              }} />
              <Box sx={{ 
                position: 'relative',
                opacity: 0.6,
                pointerEvents: 'none'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Advanced Settings
                  </Typography>
                  <Chip 
                    label="Coming Soon" 
                    size="small" 
                    color="warning" 
                    sx={{ ml: 2 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  These settings are recommended for technical users who need precise control over their model's behavior and memory.
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography gutterBottom>Temperature: {agentConfig.temperature || 0.7}</Typography>
                    <Slider
                      value={agentConfig.temperature || 0.7}
                      min={0}
                      max={1}
                      step={0.1}
                      onChange={(e, newValue) => setAgentConfig(prev => ({...prev, temperature: newValue}))}
                      valueLabelDisplay="auto"
                      marks={[
                        { value: 0, label: '0' },
                        { value: 0.5, label: '0.5' },
                        { value: 1, label: '1' }
                      ]}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Lower values = more predictable, higher values = more creative
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography gutterBottom>Top-p: {agentConfig.topP || 0.9}</Typography>
                    <Slider
                      value={agentConfig.topP || 0.9}
                      min={0.1}
                      max={1}
                      step={0.1}
                      onChange={(e, newValue) => setAgentConfig(prev => ({...prev, topP: newValue}))}
                      valueLabelDisplay="auto"
                      marks={[
                        { value: 0.1, label: '0.1' },
                        { value: 0.5, label: '0.5' },
                        { value: 1, label: '1' }
                      ]}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Controls diversity of output
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Token Limit"
                      type="number"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">tokens</InputAdornment>,
                      }}
                      value={agentConfig.tokenLimit || 2048}
                      onChange={(e) => setAgentConfig(prev => ({...prev, tokenLimit: e.target.value}))}
                      helperText="Maximum number of tokens in the response"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Maximum Context Length"
                      type="number"
                      value={agentConfig.maxContextLength || 4096}
                      onChange={(e) => setAgentConfig(prev => ({...prev, maxContextLength: e.target.value}))}
                      helperText="Maximum input context length in tokens"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Frequency Penalty"
                      type="number"
                      inputProps={{ step: 0.1, min: -2.0, max: 2.0 }}
                      value={agentConfig.frequencyPenalty || 0}
                      onChange={(e) => setAgentConfig(prev => ({...prev, frequencyPenalty: e.target.value}))}
                      helperText="Penalize repeated tokens (-2.0 to 2.0)"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Presence Penalty"
                      type="number"
                      inputProps={{ step: 0.1, min: -2.0, max: 2.0 }}
                      value={agentConfig.presencePenalty || 0}
                      onChange={(e) => setAgentConfig(prev => ({...prev, presencePenalty: e.target.value}))}
                      helperText="Penalize new tokens based on presence in text (-2.0 to 2.0)"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <FormLabel>Chunk Overlap Strategy</FormLabel>
                      <RadioGroup
                        row
                        value={agentConfig.chunkOverlap || 'default'}
                        onChange={(e) => setAgentConfig(prev => ({...prev, chunkOverlap: e.target.value}))}
                      >
                        <FormControlLabel value="default" control={<Radio />} label="Default" />
                        <FormControlLabel value="semantic" control={<Radio />} label="Semantic Boundaries" />
                        <FormControlLabel value="custom" control={<Radio />} label="Custom" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  {agentConfig.chunkOverlap === 'custom' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Custom Chunk Overlap"
                        type="number"
                        value={agentConfig.customChunkOverlap || 20}
                        onChange={(e) => setAgentConfig(prev => ({...prev, customChunkOverlap: e.target.value}))}
                        helperText="Percentage of overlap between chunks (0-50)"
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      RAG Settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>  
                      Configure how your agent uses RAG to answer questions.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Embedding Model</InputLabel>
                      <Select
                        value={agentConfig.embeddingModel || 'openai'}
                        label="Embedding Model"
                        onChange={e => setAgentConfig(prev => ({ ...prev, embeddingModel: e.target.value }))}
                      >
                        <MenuItem value="openai">OpenAI</MenuItem>
                        <MenuItem value="cohere">Cohere</MenuItem>
                        <MenuItem value="sentence-transformers">Sentence Transformers</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Chunk Size"
                      type="number"
                      value={agentConfig.chunkSize || 512}
                      onChange={e => setAgentConfig(prev => ({ ...prev, chunkSize: e.target.value }))}
                      helperText="Number of tokens per chunk (default: 512)"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Retrieval Top-K"
                      type="number"
                      value={agentConfig.retrievalTopK || 5}
                      onChange={e => setAgentConfig(prev => ({ ...prev, retrievalTopK: e.target.value }))}
                      helperText="How many top results to retrieve (default: 5)"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Similarity Metric</InputLabel>
                      <Select
                        value={agentConfig.similarityMetric || 'cosine'}
                        label="Similarity Metric"
                        onChange={e => setAgentConfig(prev => ({ ...prev, similarityMetric: e.target.value }))}
                      >
                        <MenuItem value="cosine">Cosine</MenuItem>
                        <MenuItem value="dot">Dot Product</MenuItem>
                        <MenuItem value="euclidean">Euclidean</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 4 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    Memory & Context Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Configure how your agent remembers conversations and handles context.
                  </Typography>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>Short-Term Memory</Typography>
                          <FormControl component="fieldset" sx={{ mb: 2 }}>
                            <FormLabel component="legend">Conversation Context</FormLabel>
                            <RadioGroup
                              value={agentConfig.shortTermMemory}
                              onChange={(e) => setAgentConfig(prev => ({ ...prev, shortTermMemory: e.target.value }))}
                            >
                              <FormControlLabel value="basic" control={<Radio />} label="Basic (Last 10 messages)" />
                              <FormControlLabel value="enhanced" control={<Radio />} label="Enhanced (Last 20 messages with summarization)" />
                              <FormControlLabel value="advanced" control={<Radio />} label="Advanced (Dynamic context management)" />
                            </RadioGroup>
                          </FormControl>
                          <TextField
                            fullWidth
                            label="Context Window Size (tokens)"
                            type="number"
                            value={agentConfig.contextSize}
                            onChange={(e) => setAgentConfig(prev => ({ ...prev, contextSize: e.target.value }))}
                            sx={{ mb: 2 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>Long-Term Memory</Typography>
                          <FormControl sx={{ mb: 2 }}>
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  checked={agentConfig.enableLongTermMemory}
                                  onChange={(e) => setAgentConfig(prev => ({ ...prev, enableLongTermMemory: e.target.checked }))}
                                />
                              }
                              label="Enable persistent knowledge storage"
                            />
                          </FormControl>
                          {agentConfig.enableLongTermMemory && (
                            <>
                              <FormControl component="fieldset" sx={{ mb: 2 }}>
                                <FormLabel component="legend">Memory Management</FormLabel>
                                <RadioGroup
                                  value={agentConfig.memoryManagement}
                                  onChange={(e) => setAgentConfig(prev => ({ ...prev, memoryManagement: e.target.value }))}
                                >
                                  <FormControlLabel value="automatic" control={<Radio />} label="Automatic (System managed)" />
                                  <FormControlLabel value="manual" control={<Radio />} label="Manual (User defined storage periods)" />
                                </RadioGroup>
                              </FormControl>
                              <FormControl sx={{ mb: 2 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox 
                                      checked={agentConfig.memoryRetrieval}
                                      onChange={(e) => setAgentConfig(prev => ({ ...prev, memoryRetrieval: e.target.checked }))}
                                    />
                                  }
                                  label="Enable semantic memory retrieval"
                                />
                              </FormControl>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Paper>
          </Collapse>
        </Box>
      
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={!agentConfig.selectedModelId}
          endIcon={<ArrowForwardIcon />}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

// Step 3: Integrate Tools & Triggers
export const ToolsAndTriggers = ({ agentConfig, setAgentConfig, onNext, onBack }) => {
  const tools = [
    { id: 'tool1', name: 'Web Search', description: 'Search the internet for up-to-date information', icon: <SearchIcon /> },
    { id: 'tool2', name: 'Code Execution', description: 'Execute code in Python, JavaScript, and other languages', icon: <CodeIcon /> },
    { id: 'tool3', name: 'Data Visualization', description: 'Generate charts and visualizations from data', icon: <BarChartIcon /> },
    { id: 'tool4', name: 'Image Generation', description: 'Create images based on text descriptions', icon: <ImageIcon /> },
    { id: 'tool5', name: 'Calculator', description: 'Perform complex mathematical calculations', icon: <CalculateIcon /> }
  ];

  const integrations = [
    { id: 'slack', name: 'Slack', icon: <ChatBubbleIcon /> },
    { id: 'github', name: 'GitHub', icon: <GitHubIcon /> },
    { id: 'jira', name: 'Jira', icon: <AssignmentIcon /> },
    { id: 'gmail', name: 'Gmail', icon: <EmailIcon /> },
    { id: 'gdrive', name: 'Google Drive', icon: <StorageIcon /> },
    { id: 'custom_api', name: 'Custom API', icon: <ApiIcon /> },
    { id: 'teams', name: 'Microsoft Teams', icon: <GroupsIcon /> },
    { id: 'outlook', name: 'Outlook', icon: <MailIcon /> }
  ];

  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleToolToggle = (toolId) => {
    setAgentConfig(prev => ({
      ...prev,
      tools: prev.tools.includes(toolId)
        ? prev.tools.filter(id => id !== toolId)
        : [...prev.tools, toolId]
    }));
  };

  const handleIntegrationToggle = (integrationId) => {
    setAgentConfig(prev => ({
      ...prev,
      integrations: prev.integrations.includes(integrationId)
        ? prev.integrations.filter(id => id !== integrationId)
        : [...prev.integrations, integrationId]
    }));
  };

  const handleUseDefaultPromptToggle = (checked) => {
    setAgentConfig(prev => ({
      ...prev,
      useDefaultSystemPrompt: checked,
      systemPrompt: checked ? '' : (prev.systemPrompt || 'You are an AI assistant that helps users with their tasks. You are helpful, friendly, and informative.')
    }));
  };

  const isSystemPromptValid = agentConfig.useDefaultSystemPrompt || agentConfig.systemPrompt;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Integrate Tools & Triggers</Typography>
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>1. Define Prompt Structure</Typography>
        
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={agentConfig.useDefaultSystemPrompt || false}
                onChange={(e) => handleUseDefaultPromptToggle(e.target.checked)}
              />
            }
            label="Use default system prompt from backend"
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 1 }}>
            When enabled, the backend will use its default system prompt configuration for your agent.
          </Typography>
        </Box>

        {!agentConfig.useDefaultSystemPrompt && (
          <TextField
            fullWidth
            label="System Prompt"
            value={agentConfig.systemPrompt || ''}
            onChange={(e) => setAgentConfig(prev => ({ ...prev, systemPrompt: e.target.value }))}
            multiline
            rows={16}
            placeholder="You are a helpful assistant that..."
            sx={{ mb: 2 }}
            required
            error={!agentConfig.systemPrompt}
            helperText={!agentConfig.systemPrompt ? "A system prompt is required to define your agent's behavior" : ""}
          />
        )}

        {agentConfig.useDefaultSystemPrompt && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Your agent will use the default system prompt configured on the backend. You can modify this setting later if needed.
          </Alert>
        )}
      </Box>
      
      <Box sx={{ position: 'relative', mb: 4 }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 2,
          zIndex: 1,
          pointerEvents: 'none'
        }} />
        <Box sx={{ 
          position: 'relative',
          opacity: 0.6,
          pointerEvents: 'none'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              2. Enable Tools & Functions
            </Typography>
            <Chip 
              label="Coming Soon" 
              size="small" 
              color="warning" 
              sx={{ ml: 2 }}
            />
          </Box>
          <Card sx={{ p: 2, mb: 4 }}>
            <Grid container spacing={2}>
            {tools.map((tool) => (
                <Grid item xs={12} sm={6} md={4} key={tool.id}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={agentConfig.tools.includes(tool.id)}
                            onChange={() => handleToolToggle(tool.id)}
                          />
                        }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 1 }}>
                          {tool.icon}
                    </Box>
                        <Box>
                          <Typography variant="body1">{tool.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{tool.description}</Typography>
                        </Box>
                      </Box>
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Card>
        </Box>
      </Box>
      
      <Box sx={{ position: 'relative', mb: 4 }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 2,
          zIndex: 1,
          pointerEvents: 'none'
        }} />
        <Box sx={{ 
          position: 'relative',
          opacity: 0.6,
          pointerEvents: 'none'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              3. Configure Integrations
            </Typography>
            <Chip 
              label="Coming Soon" 
              size="small" 
              color="warning" 
              sx={{ ml: 2 }}
            />
          </Box>
          <Card sx={{ p: 2, mb: 4 }}>
            <Grid container spacing={2}>
              {integrations.map((integration) => (
                <Grid item xs={12} sm={6} md={4} key={integration.id}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={agentConfig.integrations.includes(integration.id)}
                        onChange={() => handleIntegrationToggle(integration.id)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 1 }}>
                          {integration.icon}
                        </Box>
                        {integration.name}
                      </Box>
                    }
                  />
              </Grid>
            ))}
          </Grid>
          </Card>
        </Box>
      </Box>
      
      <Box sx={{ position: 'relative', mb: 4 }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 2,
          zIndex: 1,
          pointerEvents: 'none'
        }} />
        <Box sx={{ 
          position: 'relative',
          opacity: 0.6,
          pointerEvents: 'none'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              4. Select Trigger Method
            </Typography>
            <Chip 
              label="Coming Soon" 
              size="small" 
              color="warning" 
              sx={{ ml: 2 }}
            />
          </Box>
          <Card sx={{ p: 2, mb: 4 }}>
            <FormControl component="fieldset">
              <RadioGroup
                value={agentConfig.triggerMethod || 'manual'}
                onChange={(e) => setAgentConfig(prev => ({ ...prev, triggerMethod: e.target.value }))}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControlLabel 
                      value="manual" 
                      control={<Radio />} 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <SupervisorAccountIcon sx={{ mr: 1 }} />
                          <Typography>Manual (User initiated)</Typography>
                        </Box>
                      } 
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControlLabel 
                      value="scheduled" 
                      control={<Radio />} 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ScheduleIcon sx={{ mr: 1 }} />
                          <Typography>Scheduled</Typography>
                        </Box>
                      } 
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControlLabel 
                      value="webhook" 
                      control={<Radio />} 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <WebhookIcon sx={{ mr: 1 }} />
                          <Typography>Webhook/Event</Typography>
                        </Box>
                      } 
                    />
                  </Grid>
                </Grid>
              </RadioGroup>
            </FormControl>
            
            {agentConfig.triggerMethod === 'scheduled' && (
              <Box sx={{ mt: 2, pl: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Schedule Frequency</Typography>
                <RadioGroup
                  row
                  value={agentConfig.scheduleFrequency || 'daily'}
                  onChange={(e) => setAgentConfig(prev => ({ ...prev, scheduleFrequency: e.target.value }))}
                >
                  <FormControlLabel value="hourly" control={<Radio size="small" />} label="Hourly" />
                  <FormControlLabel value="daily" control={<Radio size="small" />} label="Daily" />
                  <FormControlLabel value="weekly" control={<Radio size="small" />} label="Weekly" />
                  <FormControlLabel value="monthly" control={<Radio size="small" />} label="Monthly" />
                  <FormControlLabel value="custom" control={<Radio size="small" />} label="Custom" />
                </RadioGroup>
                
                {agentConfig.scheduleFrequency === 'custom' && (
                  <TextField
                    fullWidth
                    size="small"
                    label="Cron Expression"
                    value={agentConfig.cronExpression || ''}
                    onChange={(e) => setAgentConfig(prev => ({ ...prev, cronExpression: e.target.value }))}
                    placeholder="0 9 * * 1-5"
                    helperText="Use cron format: minute hour day month weekday"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            )}
            
            {agentConfig.triggerMethod === 'webhook' && (
              <Box sx={{ mt: 2, pl: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Webhook Source</Typography>
                <RadioGroup
                  row
                  value={agentConfig.webhookSource || 'any'}
                  onChange={(e) => setAgentConfig(prev => ({ ...prev, webhookSource: e.target.value }))}
                >
                  <FormControlLabel value="any" control={<Radio size="small" />} label="Any Source" />
                  <FormControlLabel value="authorized" control={<Radio size="small" />} label="Authorized Sources Only" />
                </RadioGroup>
                
                {agentConfig.webhookSource === 'authorized' && (
                  <TextField
                    fullWidth
                    size="small"
                    label="Authorized Sources (comma-separated)"
                    value={agentConfig.authorizedSources || ''}
                    onChange={(e) => setAgentConfig(prev => ({ ...prev, authorizedSources: e.target.value }))}
                    placeholder="api.example.com, webhook.site"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            )}
          </Card>
        </Box>
      </Box>
      
      <Box sx={{ mt: 3, mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={showAdvanced ? <ExpandMoreIcon /> : <SettingsIcon />}
          onClick={() => setShowAdvanced(!showAdvanced)}
          sx={{ mb: 2 }}
        >
          {showAdvanced ? 'Hide Advanced Settings' : 'Show Advanced Settings'}
        </Button>

        <Collapse in={showAdvanced}>
          <Paper sx={{ 
            p: 3, 
            bgcolor: 'background.paper', 
            mt: 2, 
            mb: 3, 
            borderRadius: 2, 
            borderLeft: '4px solid', 
            borderColor: 'primary.main',
            position: 'relative'
          }}>
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 2,
              zIndex: 1,
              pointerEvents: 'none'
            }} />
            <Box sx={{ 
              position: 'relative',
              opacity: 0.6,
              pointerEvents: 'none'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Advanced Agent Configuration
                </Typography>
                <Chip 
                  label="Coming Soon" 
                  size="small" 
                  color="warning" 
                  sx={{ ml: 2 }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                These settings are recommended for technical users who need precise control over memory and context behavior.
              </Typography>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>Additional Settings</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Retry Attempts"
                      type="number"
                      InputProps={{ inputProps: { min: 1, max: 10 } }}
                      value={agentConfig.retryAttempts || 3}
                      onChange={(e) => setAgentConfig(prev => ({ ...prev, retryAttempts: e.target.value }))}
                      helperText="Maximum number of retry attempts for failed operations"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Error Handling</InputLabel>
                      <Select
                        value={agentConfig.errorHandling || 'retry'}
                        onChange={(e) => setAgentConfig(prev => ({ ...prev, errorHandling: e.target.value }))}
                        label="Error Handling"
                      >
                        <MenuItem value="retry">Retry</MenuItem>
                        <MenuItem value="ignore">Ignore</MenuItem>
                        <MenuItem value="fallback">Use Fallback</MenuItem>
                        <MenuItem value="terminate">Terminate</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Task Priority</InputLabel>
                      <Select
                        value={agentConfig.taskPriority || 'normal'}
                        onChange={(e) => setAgentConfig(prev => ({ ...prev, taskPriority: e.target.value }))}
                        label="Task Priority"
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="normal">Normal</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="critical">Critical</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Output Validation</InputLabel>
                      <Select
                        value={agentConfig.outputValidation || 'none'}
                        onChange={(e) => setAgentConfig(prev => ({ ...prev, outputValidation: e.target.value }))}
                        label="Output Validation"
                      >
                        <MenuItem value="none">None</MenuItem>
                        <MenuItem value="schema">Schema Validation</MenuItem>
                        <MenuItem value="semantic">Semantic Validation</MenuItem>
                        <MenuItem value="human">Human-in-the-loop</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Paper>
        </Collapse>
      </Box>
      
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={!isSystemPromptValid}
          endIcon={<ArrowForwardIcon />}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

// Step 4: Deployment & Security
export const DeploymentAndSecurity = ({ agentConfig, setAgentConfig, onBack, onDeploy }) => {
  const deploymentOptions = [
    { id: 'cloud', name: 'Cloud Deployment', description: 'Deploy on our managed infrastructure for optimal performance and scaling' },
    { id: 'self-hosted', name: 'Self-Hosted', description: 'Deploy on your own infrastructure for maximum control and data privacy', comingSoon: true }
  ];
  
  const apiOptions = [
    { id: 'rest', name: 'REST API', description: 'Standard HTTP API with JSON payloads' },
    { id: 'grpc', name: 'gRPC API', description: 'High-performance RPC framework for efficient communication', comingSoon: true }
  ];

  const [showAPIKeyForm, setShowAPIKeyForm] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [apiKeyName, setApiKeyName] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [ipAddress, setIpAddress] = useState('');
  const [endpoints, setEndpoints] = useState([]);
  const [nodes, setNodes] = useState({});
  const [loadingEndpoints, setLoadingEndpoints] = useState(false);
  const { keycloak } = useKeycloak();

  useEffect(() => {
    const fetchEndpoints = async () => {
      try {
        setLoadingEndpoints(true);
        await keycloak.updateToken(30);
        const response = await getAvailableEndpoints(keycloak.token);
        if (response.success && response.data) {
          const allEndpoints = response.data.endpoints || [];
          const allNodes = response.data.nodes || {};
          
          // Filter endpoints to only include those with healthy nodes
          const healthyEndpoints = allEndpoints.filter(endpoint => {
            const node = allNodes[endpoint];
            return node && (node.healthy === true || node.healthy === undefined); // Include undefined for backward compatibility
          });
          
          setEndpoints(healthyEndpoints);
          setNodes(allNodes);
        }
      } catch (error) {
        console.error("Error fetching endpoints:", error);
      } finally {
        setLoadingEndpoints(false);
      }
    };

    fetchEndpoints();
  }, [keycloak]);

  const handleDeploymentOptionSelect = (optionId) => {
    const option = deploymentOptions.find(opt => opt.id === optionId);
    if (option && !option.comingSoon) {
      setAgentConfig(prev => ({ ...prev, deploymentOption: optionId }));
    }
  };
  
  const handleApiOptionSelect = (optionId) => {
    const option = apiOptions.find(opt => opt.id === optionId);
    if (option && !option.comingSoon) {
      setAgentConfig(prev => ({ ...prev, apiType: optionId }));
    }
  };
  
  const handleEndpointSelect = (endpoint) => {
    setAgentConfig(prev => ({
      ...prev,
      endpoint: endpoint
    }));
  };
  
  const handleGpuTypeSelect = (gpuType) => {
    setAgentConfig(prev => ({ ...prev, gpuType }));
  };
  
  const handleRegionSelect = (region) => {
    setAgentConfig(prev => ({ ...prev, region }));
  };
  
  const handleRuntimeSelect = (runtime) => {
    setAgentConfig(prev => ({ ...prev, modelRuntime: runtime }));
  };
  
  const handleAddIpAddress = () => {
    if (ipAddress && !agentConfig.whitelistedIps?.includes(ipAddress)) {
      setAgentConfig(prev => ({
        ...prev,
        whitelistedIps: [...(prev.whitelistedIps || []), ipAddress]
      }));
      setIpAddress('');
    }
  };
  
  const handleRemoveIpAddress = (ip) => {
    setAgentConfig(prev => ({
      ...prev,
      whitelistedIps: prev.whitelistedIps.filter(item => item !== ip)
    }));
  };
  
  const handleGeoRestrictionToggle = (geo) => {
    setAgentConfig(prev => ({
      ...prev,
      allowedGeos: prev.allowedGeos?.includes(geo)
        ? prev.allowedGeos.filter(g => g !== geo)
        : [...(prev.allowedGeos || []), geo]
    }));
  };
  
  // Assuming hardware options come from a common file like MockData.js
  const availableNodes = hardwareOptions.slice(0, 4);
  
  const handleNodeToggle = (nodeId) => {
    setAgentConfig(prev => ({
      ...prev,
      nodes: prev.nodes.includes(nodeId)
        ? prev.nodes.filter(id => id !== nodeId)
        : [...prev.nodes, nodeId]
    }));
  };
  
  const handleAddApiKey = () => {
    if (apiKey && apiKeyName) {
      const newKey = {
        id: `key-${Date.now()}`,
        name: apiKeyName,
        key: apiKey.slice(0, 5) + '...' + apiKey.slice(-5),
        fullKey: apiKey,
        created: new Date().toISOString()
      };
      
      setAgentConfig(prev => ({
        ...prev,
        apiKeys: [...(prev.apiKeys || []), newKey]
      }));
      
      setApiKey('');
      setApiKeyName('');
      setShowAPIKeyForm(false);
    }
  };
  
  const handleDeleteApiKey = (keyId) => {
    setAgentConfig(prev => ({
      ...prev,
      apiKeys: (prev.apiKeys || []).filter(key => key.id !== keyId)
    }));
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Deployment & Security</Typography>
      
      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>1. Select Deployment Environment</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {deploymentOptions.map((option) => (
          <Grid item xs={12} sm={6} key={option.id}>
            <Card 
              sx={{ 
                cursor: option.comingSoon ? 'not-allowed' : 'pointer',
                border: agentConfig.deploymentOption === option.id ? '2px solid' : '1px solid',
                borderColor: agentConfig.deploymentOption === option.id ? 'primary.main' : 'divider',
                transition: 'all 0.2s',
                opacity: option.comingSoon ? 0.6 : 1,
                position: 'relative'
              }}
              onClick={() => handleDeploymentOptionSelect(option.id)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle1">{option.name}</Typography>
                  {option.comingSoon && (
                    <Chip 
                      label="Coming Soon" 
                      size="small" 
                      color="warning" 
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">{option.description}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {agentConfig.deploymentOption === 'cloud' && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Select Deployment Endpoint</Typography>
          {loadingEndpoints ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {endpoints.map((endpoint) => {
                const node = nodes[endpoint];
                const isHealthy = node && (node.healthy === true || node.healthy === undefined); // Backward compatibility
                
                return (
                  <Grid item xs={12} sm={6} key={endpoint}>
                    <Card 
                      sx={{ 
                        cursor: isHealthy ? 'pointer' : 'not-allowed',
                        border: agentConfig.endpoint === endpoint ? '2px solid' : '1px solid',
                        borderColor: agentConfig.endpoint === endpoint ? 'primary.main' : 'divider',
                        height: '100%',
                        opacity: isHealthy ? 1 : 0.6,
                      }}
                      onClick={() => isHealthy && handleEndpointSelect(endpoint)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1">{endpoint}</Typography>
                          {node && (
                            <Chip 
                              label={node.healthy === false ? "Unhealthy" : "Healthy"} 
                              size="small" 
                              color={node.healthy === false ? "error" : "success"}
                              variant="outlined"
                            />
                          )}
                        </Box>
                        {node && (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              Location: {node.location}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              GPU: {node.gpu} x {node.noOfGpu}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              System: {node.system}
                            </Typography>
                          </>
                        )}
                        {agentConfig.endpoint === endpoint && (
                          <Chip 
                            label="Selected" 
                            size="small" 
                            color="primary" 
                            sx={{ mt: 1 }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
              {!loadingEndpoints && endpoints.length === 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    No healthy endpoints available. Please try again later or contact support.
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </Box>
      )}
      
      <Typography variant="h6" gutterBottom>2. Select API Type</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {apiOptions.map((option) => (
          <Grid item xs={12} sm={6} key={option.id}>
            <Card 
              sx={{ 
                cursor: option.comingSoon ? 'not-allowed' : 'pointer',
                border: agentConfig.apiType === option.id ? '2px solid' : '1px solid',
                borderColor: agentConfig.apiType === option.id ? 'primary.main' : 'divider',
                transition: 'all 0.2s',
                opacity: option.comingSoon ? 0.6 : 1,
                position: 'relative'
              }}
              onClick={() => handleApiOptionSelect(option.id)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle1">{option.name}</Typography>
                  {option.comingSoon && (
                    <Chip 
                      label="Coming Soon" 
                      size="small" 
                      color="warning" 
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">{option.description}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      
      <Box sx={{ mt: 4, mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={showAdvanced ? <ExpandMoreIcon /> : <SettingsIcon />}
          onClick={() => setShowAdvanced(!showAdvanced)}
          sx={{ mb: 2 }}
        >
          {showAdvanced ? 'Hide Advanced Settings' : 'Show Advanced Settings'}
        </Button>

        <Collapse in={showAdvanced}>
          <Paper sx={{ 
            p: 3, 
            bgcolor: 'background.paper', 
            mt: 2, 
            borderRadius: 2, 
            borderLeft: '4px solid', 
            borderColor: 'primary.main',
            position: 'relative'
          }}>
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 2,
              zIndex: 1,
              pointerEvents: 'none'
            }} />
            <Box sx={{ 
              position: 'relative',
              opacity: 0.6,
              pointerEvents: 'none'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Advanced Security Settings
                </Typography>
                <Chip 
                  label="Coming Soon" 
                  size="small" 
                  color="warning" 
                  sx={{ ml: 2 }}
                />
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>IP Whitelist</Typography>
                      <Box sx={{ display: 'flex', mb: 2 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="IP Address"
                          placeholder="192.168.x.x"
                          value={ipAddress}
                          onChange={(e) => setIpAddress(e.target.value)}
                        />
                        <Button 
                          variant="contained"
                          size="small"
                          onClick={handleAddIpAddress}
                          disabled={!ipAddress}
                          sx={{ ml: 1 }}
                        >
                          Add
                        </Button>
                      </Box>
                      
                      <Box>
                        {agentConfig.whitelistedIps?.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {agentConfig.whitelistedIps.map((ip) => (
                              <Chip
                                key={ip}
                                label={ip}
                                onDelete={() => handleRemoveIpAddress(ip)}
                                size="small"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No IP addresses whitelisted
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>Geo Restriction</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Allow traffic only from selected regions:
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {['US', 'EU', 'APAC', 'LATAM', 'ME', 'AU'].map((geo) => (
                          <Chip
                            key={geo}
                            label={geo}
                            onClick={() => handleGeoRestrictionToggle(geo)}
                            color={agentConfig.allowedGeos?.includes(geo) ? 'primary' : 'default'}
                            clickable
                            sx={{ m: 0.5 }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>Role-based Access</Typography>
                      <FormControl component="fieldset" sx={{ width: '100%' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  checked={agentConfig.roleAdmin || false}
                                  onChange={(e) => setAgentConfig(prev => ({...prev, roleAdmin: e.target.checked}))}
                                />
                              }
                              label="Admin (Full control)"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  checked={agentConfig.roleViewer || false}
                                  onChange={(e) => setAgentConfig(prev => ({...prev, roleViewer: e.target.checked}))}
                                />
                              }
                              label="Viewer (Read-only access)"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  checked={agentConfig.roleOwner || false}
                                  onChange={(e) => setAgentConfig(prev => ({...prev, roleOwner: e.target.checked}))}
                                />
                              }
                              label="Owner (Administrative access)"
                            />
                          </Grid>
                        </Grid>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>Monitoring Options</Typography>
                  
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <FormControlLabel
                            control={
                              <Checkbox 
                                checked={agentConfig.enableLogging || false}
                                onChange={(e) => setAgentConfig(prev => ({...prev, enableLogging: e.target.checked}))}
                              />
                            }
                            label="Enable logs & metrics collection"
                          />
                          
                          {agentConfig.enableLogging && (
                            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                              <InputLabel>Log Retention Period</InputLabel>
                              <Select
                                value={agentConfig.logRetention || '30'}
                                onChange={(e) => setAgentConfig(prev => ({...prev, logRetention: e.target.value}))}
                                label="Log Retention Period"
                              >
                                <MenuItem value="7">7 days</MenuItem>
                                <MenuItem value="30">30 days</MenuItem>
                                <MenuItem value="90">90 days</MenuItem>
                                <MenuItem value="365">1 year</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <FormControlLabel
                            control={
                              <Checkbox 
                                checked={agentConfig.enableAlerts || false}
                                onChange={(e) => setAgentConfig(prev => ({...prev, enableAlerts: e.target.checked}))}
                              />
                            }
                            label="Allow alerting on task failures"
                          />
                          
                          {agentConfig.enableAlerts && (
                            <TextField
                              fullWidth
                              size="small"
                              label="Alert Email"
                              type="email"
                              placeholder="admin@example.com"
                              value={agentConfig.alertEmail || ''}
                              onChange={(e) => setAgentConfig(prev => ({...prev, alertEmail: e.target.value}))}
                              sx={{ mt: 2 }}
                            />
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Collapse>
      </Box>
      
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={onDeploy}
          disabled={!agentConfig.deploymentOption || !agentConfig.apiType}
          endIcon={<SecurityIcon />}
        >
          Deploy Agent
        </Button>
      </Box>
    </Box>
  );
};

// Main DeploymentWizard component
const DeploymentWizard = ({ onComplete, onUpdateAgent, onCancel }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentCompleted, setDeploymentCompleted] = useState(false);
  const [deploymentError, setDeploymentError] = useState(null);
  const { keycloak } = useKeycloak();
  const [agentConfig, setAgentConfig] = useState({
    name: '',
    purpose: '',
    tasks: '',
    templateId: '',
    
    personaStyle: 'friendly',
    agentType: 'reactive',
    initialKnowledge: 'pretrained',
    outputFormat: 'auto',
    
    systemPrompt: 'You are an AI assistant that helps users with their tasks. You are helpful, friendly, and informative.',
    useDefaultSystemPrompt: false,
    tools: [],
    integrations: ['slack', 'gdrive'],
    triggerMethod: 'manual',
    scheduleFrequency: 'daily',
    cronExpression: '',
    webhookSource: 'any',
    
    modelType: '',
    selectedModelId: '',
    model: '',
    endpoint: '',
    language: 'en',
    models: [],
    temperature: 0.7,
    topP: 0.9,
    tokenLimit: 2048,
    maxContextLength: 4096,
    frequencyPenalty: 0,
    presencePenalty: 0,
    chunkOverlap: 'default',
    customChunkOverlap: 20,
    
    documents: [],
    connectedAPIs: [],
    autoEmbeddings: true,
    websites: [],
    databases: [],
    
    shortTermMemory: 'basic',
    contextSize: '4000',
    enableLongTermMemory: false,
    memoryManagement: 'automatic',
    memoryRetrieval: true,
    
    retryAttempts: 3,
    humanReviewFallback: 'yes',
    errorHandling: 'retry',
    taskPriority: 'normal',
    outputValidation: 'none',
    
    deploymentOption: '',
    apiType: '',
    securityLevel: '',
    nodes: [],
    apiKeys: [],
    gpuType: 'a100',
    modelRuntime: 'hosted',
    externalEndpoint: '',
    replicas: 2,
    region: 'eu',
    
    language: 'en',
    customLanguage: '',
    multilingual: 'yes',
    
    whitelistedIps: [],
    allowedGeos: ['US', 'EU'],
    roleAdmin: true,
    roleViewer: true,
    roleOwner: false,
    
    enableLogging: true,
    logRetention: '30',
    enableAlerts: false,
    alertEmail: ''
  });

  // Get selected model details for display in the status overview
  const selectedModel = agentConfig.selectedModelId ? getModelById(agentConfig.selectedModelId) : null;
  
  // Create an enhanced config for the status overview that includes the model name
  const enhancedConfig = {
    ...agentConfig,
    selectedModelName: selectedModel ? selectedModel.name : ''
  };

  // Notify parent component when agentConfig or activeStep changes
  React.useEffect(() => {
    if (onUpdateAgent) {
      onUpdateAgent(enhancedConfig, activeStep);
    }
  }, [enhancedConfig, activeStep, onUpdateAgent]);

  const steps = [
    { label: 'Domain & Tasks', icon: <DomainIcon />, component: DomainAndTasks },
    { label: 'Intelligence', icon: <PsychologyIcon />, component: ConfigureIntelligence },
    { label: 'Tools & Triggers', icon: <ExtensionIcon />, component: ToolsAndTriggers },
    { label: 'Deployment', icon: <SecurityIcon />, component: DeploymentAndSecurity }
  ];

  const handleTabChange = (event, newValue) => {
    const isSystemPromptValid = agentConfig.useDefaultSystemPrompt || agentConfig.systemPrompt;
    
    if (
      (newValue === 1 && !agentConfig.name) ||
      (newValue === 2 && !agentConfig.selectedModelId) ||
      (newValue === 3 && !isSystemPromptValid) 
    ) {
      return;
    }
    setActiveStep(newValue);
  };

  const handleNext = () => {
    setActiveStep(prevStep => prevStep + 1);
  };

  const handleBack = () => {
    if (activeStep === 0 && onCancel) {
      onCancel();
    } else {
      setActiveStep(prevStep => prevStep - 1);
    }
  };

  const handleDeploy = async () => {
    setIsDeploying(true);
    setDeploymentError(null);
    
    try {
      await keycloak.updateToken(30);
      const token = keycloak.token;
      
      const agentData = {
        name: agentConfig.name,
        endpoint: agentConfig.endpoint || '',
        language: agentConfig.language || 'en',
        model: agentConfig.selectedModelId || '',
        models: agentConfig.selectedModels || [],
        useDefaultSystemPrompt: agentConfig.useDefaultSystemPrompt || false
      };

      if (!agentConfig.useDefaultSystemPrompt && agentConfig.systemPrompt) {
        agentData.system_prompt = agentConfig.systemPrompt;
      }

      if (!agentData.endpoint) {
        throw new Error("Please select a deployment endpoint");
      }
      
      if (!agentData.models || agentData.models.length === 0) {
        throw new Error("Please select at least one model");
      }
      
      const response = await createAgent(token, agentData);
      
      if (response.success) {
        setAgentConfig(prev => ({
          ...prev,
          apiResponse: response.data
        }));
        setDeploymentCompleted(true);
      } else {
        setDeploymentError(response.error || "Failed to create agent");
      }
    } catch (error) {
      console.error("Agent deployment error:", error);
      setDeploymentError(error.message || "An unexpected error occurred");
    } finally {
      setIsDeploying(false);
    }
  };
  
  const handleFinish = () => {
    // Send final configuration to parent component
    onComplete(enhancedConfig);
  };

  // Render the current step
  const StepComponent = steps[activeStep].component;
  return (
    <Box sx={{ width: '100%' }}>
      {isDeploying && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 1 }}>
            Deploying your AI Agent...
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            This may take a few moments while we configure your agent
          </Typography>
          {deploymentError && (
            <Alert severity="error" sx={{ mt: 2, mx: 'auto', maxWidth: 600 }}>
              {deploymentError}
            </Alert>
          )}
        </Box>
      )}

      {!isDeploying && !deploymentCompleted && (
        <>
          <Tabs
            value={activeStep}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
          >
            {steps.map((step, index) => {
              const isSystemPromptValid = agentConfig.useDefaultSystemPrompt || agentConfig.systemPrompt;
              
              return (
                <Tab
                  key={index}
                  label={step.label}
                  icon={step.icon}
                  iconPosition="start"
                  disabled={
                    (index === 1 && !agentConfig.name) ||
                    (index === 2 && !agentConfig.selectedModelId) ||
                    (index === 3 && !isSystemPromptValid)
                  }
                  sx={{
                    minHeight: 48,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center'
                  }}
                />
              );
            })}
          </Tabs>

          <StepComponent
            agentConfig={agentConfig}
            setAgentConfig={setAgentConfig}
            onNext={handleNext}
            onBack={handleBack}
            onDeploy={handleDeploy}
            onCancel={onCancel}
          />
        </>
      )}

      {deploymentCompleted && (
        <Box sx={{ p: 5, textAlign: 'center' }}>
          <CelebrationIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
          <Typography variant="h4" gutterBottom>Deployment Successful!</Typography>
          <Typography variant="body1" sx={{ mb: 4 }}>
            Your AI agent "{agentConfig.name}" has been successfully deployed and is now online.
          </Typography>
          <Card sx={{ mb: 4, p: 3, maxWidth: 600, mx: 'auto', textAlign: 'left' }}>
            <Typography variant="h6" gutterBottom>Deployment Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}><Typography variant="body2" color="text.secondary">Agent ID:</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{agentConfig.apiResponse?.agent?.id || '-'}</Typography></Grid>
              <Grid item xs={4}><Typography variant="body2" color="text.secondary">Agent Name:</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{agentConfig.name || '-'}</Typography></Grid>
              <Grid item xs={4}><Typography variant="body2" color="text.secondary">API Key:</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{agentConfig.apiResponse?.agent?.apiKey || '-'}</Typography></Grid>
              <Grid item xs={4}><Typography variant="body2" color="text.secondary">Endpoint:</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{agentConfig.apiResponse?.agent?.endpoint || '-'}</Typography></Grid>
              <Grid item xs={4}><Typography variant="body2" color="text.secondary">Active:</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{agentConfig.apiResponse?.agent?.isActive ? 'Yes' : 'No'}</Typography></Grid>
              <Grid item xs={4}><Typography variant="body2" color="text.secondary">User ID:</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{agentConfig.apiResponse?.agent?.userId || '-'}</Typography></Grid>
              <Grid item xs={4}><Typography variant="body2" color="text.secondary">Created At:</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{agentConfig.apiResponse?.agent?.createdAt ? new Date(agentConfig.apiResponse.agent.createdAt).toLocaleString() : '-'}</Typography></Grid>
              <Grid item xs={4}><Typography variant="body2" color="text.secondary">Updated At:</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{agentConfig.apiResponse?.agent?.updatedAt ? new Date(agentConfig.apiResponse.agent.updatedAt).toLocaleString() : '-'}</Typography></Grid>
            </Grid>
          </Card>
          <Button
            variant="contained"
            size="large"
            onClick={handleFinish}
            sx={{ px: 4 }}
          >
            Return to Dashboard
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default DeploymentWizard; 